import _debounce from 'lodash/debounce';
import { ChangeEvent, createContext, useCallback, useContext } from 'react';
import moment from 'moment';
import { useInputHelper } from '@src/utils/inputs-helper';
import { ReactNode } from 'react';

type SubscriptionContextStateType = {
  page: number;
  order: string;
  orderBy: string;
  rowsPerPage: number;
  search: string;
  debouncedSearch: string;
  filterSidebarOpen: boolean;
  fromdate: string | null;
  todate: string | null;
  status: string;
  planType: string; // "" | "WEEKLY" | "MONTHLY"
};

type SubscriptionFilterFormType = {
  status?: string;
  planType?: string;
  fromDate?: string;
  toDate?: string;
};

type SubscriptionContextValueType = {
  state: SubscriptionContextStateType;
  onDispatch: (key: string) => (value: any) => void;
  handlePageChange: (event: unknown, newPage: number) => void;
  handleRequestSort: (property: string) => void;
  handleRowsPerPageChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSearch: (value: string) => void;
  handleToggleFilterSidebar: (value?: boolean) => void;
  handleApplyFilter: (formData: SubscriptionFilterFormType) => void;
  handleClearFilters: () => void;
  handleTabChange: (planType: string) => void;
};

const initialState: SubscriptionContextStateType = {
  page: 0,
  order: 'desc',
  orderBy: 'transactionDate',
  rowsPerPage: 10,
  search: '',
  debouncedSearch: '',
  filterSidebarOpen: false,
  fromdate: null,
  todate: null,
  status: '',
  planType: '',
};

export const SubscriptionContext = createContext<SubscriptionContextValueType>({} as SubscriptionContextValueType);

const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const { state, onDispatch } = useInputHelper(initialState);

  const handlePageChange = (_: unknown, newPage: number) => {
    onDispatch('page')(newPage);
  };

  const handleRequestSort = (property: string) => {
    const isAsc = state.orderBy === property && state.order === 'asc';
    onDispatch('order')(isAsc ? 'desc' : 'asc');
    onDispatch('orderBy')(property);
  };

  const handleRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
    onDispatch('rowsPerPage')(event.target.value);
  };

  const debouncedSearchFn = (value: string) => {
    onDispatch('page')(0);
    onDispatch('debouncedSearch')(value);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedSearch = useCallback(_debounce(debouncedSearchFn, 1000), []);

  const handleSearch = (value: string) => {
    onDispatch('search')(value);
    handleDebouncedSearch(value);
  };

  const handleToggleFilterSidebar = (value?: boolean) => {
    if (typeof value === 'boolean') {
      onDispatch('filterSidebarOpen')(value);
      return;
    }
    onDispatch('filterSidebarOpen')(!state.filterSidebarOpen);
  };

  const handleApplyFilter = (formData: SubscriptionFilterFormType) => {
    onDispatch('page')(0);
    onDispatch('status')(formData.status || '');
    onDispatch('fromdate')(formData.fromDate ? moment(formData.fromDate).format('YYYY-MM-DD') : null);
    onDispatch('todate')(formData.toDate ? moment(formData.toDate).format('YYYY-MM-DD') : null);
    onDispatch('filterSidebarOpen')(false);
  };

  const handleClearFilters = () => {
    onDispatch('search')('');
    onDispatch('debouncedSearch')('');
    onDispatch('page')(0);
    onDispatch('status')('');
    onDispatch('fromdate')(null);
    onDispatch('todate')(null);
    onDispatch('filterSidebarOpen')(false);
  };

  const handleTabChange = (planType: string) => {
    onDispatch('planType')(planType);
    onDispatch('page')(0);
  };

  const value: SubscriptionContextValueType = {
    state,
    onDispatch,
    handlePageChange,
    handleRequestSort,
    handleRowsPerPageChange,
    handleSearch,
    handleToggleFilterSidebar,
    handleApplyFilter,
    handleClearFilters,
    handleTabChange,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscriptionContext = () => useContext(SubscriptionContext);

export default SubscriptionProvider;
